import React, { useContext, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { LanguageContext } from '../hooks/useLanguage';

gsap.registerPlugin(ScrollTrigger);

const content = {
  label: {
    es: 'Eficiencia energética',
    en: 'Energy efficiency',
    fr: "Efficacité énergétique",
  },
  title: {
    es: 'Un edificio inteligente consume menos. Sin esfuerzo.',
    en: 'An intelligent building consumes less. Effortlessly.',
    fr: "Un bâtiment intelligent consomme moins. Sans effort.",
  },
  subtitle: {
    es: 'La automatización bien diseñada reduce el consumo de forma pasiva. Cada zona controlada por presencia, cada persiana que sigue la trayectoria solar, cada circuito que se desconecta sin intervención manual — suma en la factura y suma en el confort.',
    en: 'Well-designed automation reduces consumption passively. Every presence-controlled zone, every blind tracking the sun, every circuit that disconnects without manual input — adds up on the bill and adds up in comfort.',
    fr: "L'automatisation bien conçue réduit la consommation passivement. Chaque zone contrôlée par présence, chaque volet suivant la trajectoire solaire, chaque circuit se déconnectant sans intervention manuelle — se cumule sur la facture et sur le confort.",
  },
  stats: [
    {
      prefix: '',
      countTo: 60,
      suffix: '%',
      display: '60%',
      label: {
        es: 'Reducción media en consumo de iluminación con control DALI y sensores de presencia',
        en: 'Average reduction in lighting consumption with DALI control and presence sensors',
        fr: "Réduction moyenne de la consommation d'éclairage avec contrôle DALI et capteurs de présence",
      },
    },
    {
      prefix: '',
      countTo: 7,
      suffix: '',
      unit: { es: 'años', en: 'years', fr: 'ans' },
      display: '7',
      label: {
        es: 'Periodo de retorno de inversión habitual en instalaciones residenciales y comerciales',
        en: 'Typical return on investment period for residential and commercial installations',
        fr: "Période de retour sur investissement habituelle pour les installations résidentielles et commerciales",
      },
    },
    {
      prefix: '',
      countTo: 100,
      suffix: '%',
      display: '100%',
      label: {
        es: 'Monitorización del consumo en tiempo real. Cada circuito. Desde la pantalla táctil o la app.',
        en: 'Real-time consumption monitoring. Every circuit. From the touch panel or the app.',
        fr: "Surveillance de la consommation en temps réel. Chaque circuit. Depuis l'écran tactile ou l'application.",
      },
    },
  ],
  callout: {
    es: 'Una vivienda de 200 m² con control de iluminación por zonas y gestión de persianas puede reducir su consumo eléctrico entre un 25 y un 40% respecto a una instalación convencional. El sistema KNX no es un gasto — es una decisión de ingeniería.',
    en: 'A 200 m² home with zone lighting control and blind management can reduce its electricity consumption by 25 to 40% compared to a conventional installation. A KNX system is not an expense — it is an engineering decision.',
    fr: "Un logement de 200 m² avec contrôle d'éclairage par zones et gestion des volets peut réduire sa consommation électrique de 25 à 40% par rapport à une installation conventionnelle. Un système KNX n'est pas une dépense — c'est une décision d'ingénierie.",
  },
};

interface StatCardProps {
  stat: typeof content.stats[0];
  lang: 'es' | 'en' | 'fr';
  index: number;
}

const StatCard: React.FC<StatCardProps> = ({ stat, lang, index }) => {
  const numRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const num = numRef.current;
    const card = cardRef.current;
    const line = lineRef.current;
    if (!num || !card || !line) return;

    const obj = { val: 0 };

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: card,
        start: 'top 80%',
        once: true,
      },
    });

    // Line draws in first
    tl.fromTo(line,
      { scaleX: 0 },
      { scaleX: 1, duration: 0.6, ease: 'power2.out', transformOrigin: 'left' }
    );

    // Card fades up
    tl.fromTo(card,
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' },
      '-=0.3'
    );

    // Number counts up
    tl.to(obj, {
      val: stat.countTo,
      duration: 1.8,
      ease: 'power2.out',
      onUpdate: () => {
        num.textContent = Math.round(obj.val) + stat.suffix;
      },
    }, '-=0.2');

    return () => {
      ScrollTrigger.getAll().forEach(st => {
        if (st.vars.trigger === card) st.kill();
      });
    };
  }, [stat]);

  return (
    <div
      ref={cardRef}
      style={{
        background: '#1A1A1A',
        padding: 'clamp(32px, 3vw, 56px)',
        opacity: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated gold top bar */}
      <div
        ref={lineRef}
        style={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, #C8A96E, #E8C98A)',
          transformOrigin: 'left',
          transform: 'scaleX(0)',
        }}
      />

      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 'clamp(36px, 4vw, 52px)',
        fontWeight: 400,
        color: '#C8A96E',
        lineHeight: 1,
        marginBottom: '8px',
      }}>
        <span ref={numRef}>0{stat.suffix}</span>
        {'unit' in stat && stat.unit && (
          <span style={{ fontSize: '0.4em', marginLeft: '6px', color: '#A88848' }}>
            {(stat.unit as any)[lang]}
          </span>
        )}
      </div>

      <p style={{
        fontFamily: 'var(--font-body)',
        fontSize: '13px',
        color: '#555555',
        lineHeight: 1.7,
        marginTop: '16px',
      }}>
        {stat.label[lang]}
      </p>
    </div>
  );
};

const Energy: React.FC = () => {
  const { lang } = useContext(LanguageContext);
  const sectionRef = useRef<HTMLElement>(null);

  // Subtle background grid parallax
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const grid = section.querySelector('.energy-bg') as HTMLElement;
    if (!grid) return;

    gsap.to(grid, {
      yPercent: -15,
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
      },
    });

    return () => ScrollTrigger.getAll().forEach(st => st.kill());
  }, []);

  return (
    <section
      id="ahorro"
      ref={sectionRef}
      style={{
        background: '#0D0D0D',
        padding: 'clamp(60px, 8vw, 120px) 0',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Parallax KNX grid */}
      <div className="energy-bg" style={{
        position: 'absolute',
        inset: '-20%',
        backgroundImage: 'linear-gradient(rgba(200,169,110,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(200,169,110,0.04) 1px, transparent 1px)',
        backgroundSize: '20px 20px',
      }} />

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ maxWidth: '720px', marginBottom: '80px' }}
        >
          <span style={{
            fontFamily: 'var(--font-body)',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '5px',
            textTransform: 'uppercase',
            color: '#C8A96E',
            display: 'block',
            marginBottom: '12px',
          }}>
            {content.label[lang]}
          </span>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(26px, 3.5vw, 48px)',
            fontWeight: 700,
            letterSpacing: '-0.5px',
            lineHeight: 1.1,
            color: '#FFFFFF',
            marginBottom: '24px',
          }}>
            {content.title[lang]}
          </h2>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'clamp(15px, 1.2vw, 18px)',
            color: '#555555',
            lineHeight: 1.8,
          }}>
            {content.subtitle[lang]}
          </p>
        </motion.div>

        {/* Stat cards — GSAP animated */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '2px',
          marginBottom: '2px',
        }} className="energy-grid">
          {content.stats.map((stat, i) => (
            <StatCard key={i} stat={stat} lang={lang} index={i} />
          ))}
        </div>

        {/* Callout bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          style={{
            background: '#1A1A1A',
            borderLeft: '3px solid #C8A96E',
            padding: 'clamp(24px, 2.5vw, 40px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '40px',
            flexWrap: 'wrap',
          }}
        >
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'clamp(14px, 1.1vw, 16px)',
            color: '#AAAAAA',
            lineHeight: 1.8,
            maxWidth: '760px',
          }}>
            {content.callout[lang]}
          </p>
          <motion.a
            href="#contact"
            whileHover={{ background: '#C8A96E', color: '#0D0D0D' }}
            style={{
              padding: '14px 32px',
              border: '1px solid #C8A96E',
              fontFamily: 'var(--font-body)',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '3px',
              textTransform: 'uppercase',
              color: '#C8A96E',
              transition: 'all 0.3s',
              whiteSpace: 'nowrap',
              display: 'inline-block',
              flexShrink: 0,
            }}
          >
            {lang === 'es' ? 'Calcular ahorro' : lang === 'en' ? 'Discuss savings' : 'Calculer les économies'}
          </motion.a>
        </motion.div>

      </div>
      <style>{`
        @media (max-width: 768px) { .energy-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
};

export default Energy;
